import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Text } from '@chakra-ui/react';

import LoadingSpinner from '../../../../../../components/Spinner';
import UserBadge from '../../../../../../components/UserBadge';
import { CypressIds } from '../../../../../../constants/cypressIds';
import { inAppText } from '../../../../../../constants/inAppText';
import { selectCurrentlyUpdatingJob, selectJobProfile } from '../../../../../../redux/modules/jobs';
import { getDateObject } from '../../../../../../utils/dateUtil';
import JobDescription from '../JobDescription';
import JobInfoSection from '../JobInfoSection';

const JobInfoDisplay = () => {
  const job = useSelector(selectJobProfile);
  const jobUpdating = useSelector(selectCurrentlyUpdatingJob);
  const isArchived = useMemo(() => !!job?.archivedAt, [job]);

  const jobRange = useMemo(() => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const jobStart = getDateObject(job.jobStart);
    const jobEnd = getDateObject(job.jobEnd);

    if (!jobStart && !jobEnd) {
      return '-';
    }

    let jobStartString = '?';
    let jobEndString = '?';

    if (jobStart) {
      jobStartString = `${months[jobStart.getMonth()]} ${jobStart.getDate()}, ${jobStart.getFullYear()}`;
    }

    if (jobEnd) {
      jobEndString = `${months[jobEnd.getMonth()]} ${jobEnd.getDate()}, ${jobEnd.getFullYear()}`;
    }

    return `${jobStartString} - ${jobEndString}`;
  }, [job]);

  if (!job) {
    return (<LoadingSpinner />);
  }

  return (
    <>
      <JobInfoSection title="Project Name">
        <Text textStyle="bodyXxl600">{job.jobName ?? '-'}</Text>
      </JobInfoSection>

      <JobInfoSection title="Project Number">
        <Text textStyle="bodyLg600">{job.jobNumber ?? '-'}</Text>
      </JobInfoSection>

      <JobInfoSection title="Project Description">
        <JobDescription jobDescription={job.jobDescription} />
      </JobInfoSection>

      <JobInfoSection title="Project Dates">
        <Text textStyle="bodyMd500">{jobRange}</Text>
      </JobInfoSection>

      <JobInfoSection title="Project Owner" toolTip={inAppText.tooltips.projectDetails.projectInformation.projectOwner}>
        {job.ownerId
          ? (
            <UserBadge
              disabled={isArchived}
              userFirstName={job?.currentOwner?.userFirstName}
              userLastName={job?.currentOwner?.userLastName}
            />
          )
          : (<Text textStyle="bodyMd500">-</Text>)}
      </JobInfoSection>

      <JobInfoSection title="Department" toolTip={inAppText.tooltips.projectDetails.projectInformation.department}>
        <Text textStyle="bodyMd500">{(job?.department?.displayName || job?.department?.departmentName) ?? '-'}</Text>
      </JobInfoSection>

      <JobInfoSection
        cypressId={CypressIds.projectRequirementTemplateDisplay}
        title="Project Requirements"
        toolTip={inAppText.tooltips.projectDetails.projectInformation.projectRequirements}
      >
        {jobUpdating
          ? (<LoadingSpinner isInline minHeight="50px" spinnerSize="md" />)
          : (<Text textStyle="bodyMd500">{job?.currentRequirements?.name ?? '-'}</Text>)}
      </JobInfoSection>
    </>
  );
};

export default JobInfoDisplay;
